import { MessageCircle, MessageSquareText, Phone, X } from "lucide-react";
import { useState } from "react";
import {
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL_URL,
  CONTACT_SMS_URL,
  CONTACT_WHATSAPP_URL,
} from "@/lib/contact";

type ContactMethod = "sms" | "whatsapp" | "call";

function trackContactMethod(method: ContactMethod) {
  if (typeof window === "undefined") return;

  const contactWindow = window as Window & {
    dataLayer?: Array<Record<string, unknown>>;
    gtag_report_phone_conversion?: () => void;
  };

  contactWindow.dataLayer?.push({
    event: "contact_method_click",
    contact_method: method,
  });

  if (method === "call") {
    contactWindow.gtag_report_phone_conversion?.();
  }
}

export default function FloatingContact() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      {isOpen && (
        <section
          id="floating-contact-options"
          aria-label="Contact Holowaty Homes"
          className="w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-[oklch(0.85_0.03_85)] bg-white p-4 shadow-2xl"
        >
          <div className="mb-3 pr-8">
            <p className="font-serif text-lg font-bold text-[oklch(0.25_0.1_145)]">Let&apos;s talk</p>
            <p className="mt-1 text-sm leading-relaxed text-[oklch(0.42_0.03_145)]">
              Choose the easiest way to reach our local team.
            </p>
          </div>

          <div className="grid gap-2">
            <a
              href={CONTACT_SMS_URL}
              onClick={() => trackContactMethod("sms")}
              className="flex min-h-12 items-center gap-3 rounded-xl bg-[oklch(0.25_0.1_145)] px-4 py-3 font-semibold text-white transition-colors hover:bg-[oklch(0.32_0.11_145)] focus:outline-none focus:ring-2 focus:ring-[oklch(0.75_0.15_85)] focus:ring-offset-2"
            >
              <MessageSquareText className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span>Text us</span>
            </a>
            <a
              href={CONTACT_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContactMethod("whatsapp")}
              className="flex min-h-12 items-center gap-3 rounded-xl bg-[#25D366] px-4 py-3 font-semibold text-[#073b1a] transition-colors hover:bg-[#1ebe5d] focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2"
            >
              <MessageCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span>Message on WhatsApp</span>
            </a>
            <a
              href={CONTACT_PHONE_TEL_URL}
              onClick={() => trackContactMethod("call")}
              className="flex min-h-12 items-center gap-3 rounded-xl border border-[oklch(0.75_0.15_85)] bg-[oklch(0.98_0.01_85)] px-4 py-3 font-semibold text-[oklch(0.25_0.1_145)] transition-colors hover:bg-[oklch(0.95_0.04_85)] focus:outline-none focus:ring-2 focus:ring-[oklch(0.75_0.15_85)] focus:ring-offset-2"
            >
              <Phone className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span>Call {CONTACT_PHONE_DISPLAY}</span>
            </a>
          </div>

          <p className="mt-3 text-center text-xs text-[oklch(0.52_0.02_145)]">
            No pressure. We&apos;ll help you explore your options.
          </p>
        </section>
      )}

      <button
        type="button"
        aria-label={isOpen ? "Close contact options" : "Open contact options"}
        aria-expanded={isOpen}
        aria-controls="floating-contact-options"
        onClick={() => setIsOpen((open) => !open)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-[oklch(0.75_0.15_85)] font-bold text-[oklch(0.2_0.03_145)] shadow-xl transition-all hover:-translate-y-0.5 hover:bg-[oklch(0.67_0.15_85)] focus:outline-none focus:ring-2 focus:ring-[oklch(0.75_0.15_85)] focus:ring-offset-2 sm:w-auto sm:gap-2 sm:px-5"
      >
        {isOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <MessageCircle className="h-5 w-5" aria-hidden="true" />}
        <span className="hidden sm:inline">{isOpen ? "Close" : "Contact us"}</span>
      </button>
    </div>
  );
}
